import Hotel from '@app/model/hotel/Hotel';
import { ModalRefProps } from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import React, { useCallback, useState } from 'react';
import FormButton from '@app/component/form/button/FormButton';
import Message from '@app/component/message/Message';
import FormInputText from '@app/component/form/input/text/FormInputText';
import { useForm } from 'react-hook-form';
import { useTranslation } from '@app/component/translation/Translation';
import { useMutation } from '@tanstack/react-query';
import HotelService from '@app/model/hotel/service/HotelService';

type AddNewHotelFromBookingUrlModalProps = {
	onCreate: (hotel: Hotel) => any,
	modal: ModalRefProps
}

const AddNewHotelFromBookingUrlModal = React.memo(
	function AddNewHotelFromBookingUrlModal({ onCreate, modal }: AddNewHotelFromBookingUrlModalProps) {
		const form = useForm();
		const [isProcessing, setIsProcessing] = useState(false);
		const { t } = useTranslation();

		const submit = useCallback((data: { url: string }) => {
			const regex = /https:\/\/www\.?booking\.com\/hotel/gm;
			if (!data.url.match(regex)) {
				form.setError('url', { message: 'Please use correct booking url' });
			} else {
				addHotelFromBookingUrl.mutate(data.url);
			}
		}, [onCreate]);

		const addHotelFromBookingUrl = useMutation({
			mutationFn: HotelService.addHotelFromBookingUrl,
			onMutate: () => setIsProcessing(true),
			onSuccess: (data) => {
				onCreate(data);
				modal.close();
			},
			onError: (e: string) => form.setError('url', { message: t(e) }),
			onSettled: () => setIsProcessing(false),
		});

		return <Modal title="Insert Hotel's Booking Link" ref={modal.ref} actions={
			<FormButton state="primary" isLoading={addHotelFromBookingUrl.isPending || isProcessing}
									onClick={form.handleSubmit(submit)}>
				Submit
			</FormButton>
		}>
			<Message state="info">
				<div>Provide the hotel's <span className="font-semibold">Booking.com</span> link below. Once added, you can set
					it
					as your property or as a competitor.
				</div>
			</Message>
			<div className="mt-8">
				<FormInputText label="Set booking link" name="url" form={form} disabled={addHotelFromBookingUrl.isPending}
											 placeholder="https://www.booking.com/...." />
			</div>
		</Modal>;
	});

export default AddNewHotelFromBookingUrlModal;